/**
 * `/check-email` — the page that says a link is on its way.
 *
 * Reached after registering and after asking for a password reset. Both say
 * essentially the same thing, and both are the moment the user leaves the
 * portal for their inbox, so the address is echoed back: a typo is invisible
 * once the form is gone, and "no email arrived" is otherwise indistinguishable
 * from "it went to the wrong place".
 */

import { Link, useLocation } from "react-router-dom";

import { AuthCard } from "./AuthCard";

interface CheckEmailState {
  email?: string;
  reason?: "register" | "reset";
}

export function CheckEmail() {
  const { email, reason } = (useLocation().state ?? {}) as CheckEmailState;

  return (
    <AuthCard
      title="Check your email"
      footer={
        <>
          Back to <Link to="/login">signing in</Link>.
        </>
      }
    >
      <p>
        {reason === "reset"
          ? "If that address has an account, a link to choose a new password is on its way to "
          : "To finish setting up your account, follow the link we've just sent to "}
        {email ? <strong>{email}</strong> : "the address you gave"}.
      </p>
      <p className="muted">
        The link expires in an hour. If it doesn't arrive, check your spam folder — and if it still
        hasn't,{" "}
        {reason === "reset" ? (
          <Link to="/forgot-password">ask for another one</Link>
        ) : (
          // Not "register again": a repeat registration answers with the same
          // generic success it gives a new one — deliberately, so the endpoint
          // cannot be used to discover which addresses have accounts — and
          // sends no second email. Signing in is what mints a fresh link, and
          // it is the only thing that does.
          <Link to="/login">sign in to have another sent</Link>
        )}
        ; nothing is lost by doing so.
      </p>
      <p className="auth__watch">
        <img
          className="auth__watch__image"
          src="/bear/bear-asleep.webp"
          alt=""
          loading="lazy"
          decoding="async"
        />
        <span className="bear-line">
          Big Bear is watching the door while you check your inbox.
        </span>
      </p>
    </AuthCard>
  );
}
